<script setup lang="ts">
//#region imports
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFormatters } from '@/composables/useFormatters';
import type { EmitsToListeners } from '@/types/core';
import type { GetCpaConversionsByIncrementingIdCpaPayoutsResponse } from '@/types/openapi-ts/types.gen';
//#endregion

//#region types
type ConversionState = GetCpaConversionsByIncrementingIdCpaPayoutsResponse['payload']['rule_cpa_payouts'][number];

export type ConversionSelectPayload = { conversionId: number; cpaRuleIndex: number; data: string };

export interface CpaConversionMoveToHoldParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}

export interface Props {
  conversionId: number;
  conversionStates?: ConversionState[];
  data: string;
}

export interface Emits {
  close: [];
  select: [payload: ConversionSelectPayload];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  conversionStates: () => [],
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
const { formatCurrencyValue, formatDuration, formatValuePerDays } = useFormatters();
//#endregion

//#region state
const isVisible = ref(false);
const selectedIndex = ref<number | null>(null);
//#endregion

//#region methods
function getCapPlayersText(state: ConversionState) {
  if (state.cap_players_count === 0) {
    return '0';
  }

  const days = formatValuePerDays(state.cap_period_seconds);

  return t(
    'payments.cpa_conversions.details.cap_players_per_days',
    { playersCount: state.cap_players_count, days, count: days },
  );
}

function getMetricItems(state: ConversionState) {
  return [
    {
      key: 'payout',
      title: t('payments.cpa_conversions.details.metrics.payout'),
      value: formatCurrencyValue(state.currency_code, state.amount ?? null),
    },
    {
      key: 'cap_players',
      title: t('payments.cpa_conversions.details.metrics.cap_players'),
      value: getCapPlayersText(state),
    },
    {
      key: 'hold',
      title: t('payments.cpa_conversions.details.metrics.hold'),
      value: formatDuration(state.hold_seconds),
    },
  ];
}

function handleSelect(index: number) {
  if (selectedIndex.value === index) {
    selectedIndex.value = null;
    return;
  }

  selectedIndex.value = index;
}

function open() {
  selectedIndex.value = null;
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSubmit() {
  if (selectedIndex.value === null) {
    return;
  }

  emit('select', {
    conversionId: props.conversionId,
    cpaRuleIndex: selectedIndex.value,
    data: props.data,
  });
  handleClose();
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('payments.cpa_conversions.move_to_hold.title')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="cpa-move-popup t:space-y-16">
        <UiLabel :label="$t('payments.cpa_conversions.move_to_hold.select_rules')" />
        <div
          v-for="(state, index) in props.conversionStates"
          :key="index"
        >
          <CardTitle>
            <UiCheckbox
              label="CPA"
              bold
              :model-value="selectedIndex === index"
              @change="handleSelect(index)"
            />
          </CardTitle>
          <div class="t:pl-8">
            <CardItem
              v-for="(metric, index) in getMetricItems(state)"
              :key="metric.key"
              :no-border="index === 0"
              :item="{
                name: metric.title,
                value: metric.value,
              }"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          type="positive"
          size="big"
          icon="check"
          filled
          :disabled="selectedIndex === null"
          @click="handleSubmit"
        >
          {{ $t('payments.cpa_conversions.move_to_hold.select') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
